// src/components/PredictionDashboard/BreakdownResultTable.tsx
import React, { useState, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  IconButton, Collapse, LinearProgress, Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

// Iconos
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Tipos e Interfaces
import { ViewMode } from '../../types/predictions';
import { getPredictionBreakdownChildren, ScenarioRow } from '../../services/predictionService';

interface BreakdownResultTableProps {
  data: ScenarioRow[]; 
  isLoading: boolean;
  viewMode: ViewMode;
  currentDate: string;
  searchTerm: string;
}

// --- HELPERS VISUALES ---
const fmtCurrency = (v: number) => v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtNum = (v: number) => Math.round(v).toLocaleString('es-ES');

const SignalStrengthIndicator: React.FC<{ level: string }> = ({ level }) => {
    // 1. Determinar nivel numérico (1-3)
    const activeBars = level === 'HIGH' ? 3 : level === 'MEDIUM' ? 2 : 1;
    
    // 2. Determinar Color (Semántico)
    const color = level === 'HIGH' ? '#4ade80' : level === 'MEDIUM' ? '#38bdf8' : '#64748b'; // Verde, Azul, Gris
    
    // 3. Tooltip explicativo
    const tooltipText = level === 'HIGH' ? 'Fiabilidad alta (muchos datos)' 
                      : level === 'MEDIUM' ? 'Fiabilidad media' 
                      : 'Fiabilidad baja (pocos datos)';

    return (
        <Tooltip title={tooltipText} placement="top" arrow>
            <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.3, height: 16, cursor: 'help', verticalAlign: 'middle', ml: 1 }}>
                {[1, 2, 3].map((barNum) => (
                    <Box 
                        key={barNum}
                        sx={{
                            width: 3,
                            height: barNum * 4 + 2, // Escalera visual: 6px, 10px, 14px
                            borderRadius: 0.5,
                            bgcolor: barNum <= activeBars ? color : 'rgba(255,255,255,0.1)',
                            transition: 'all 0.3s ease'
                        }}
                    />
                ))}
            </Box>
        </Tooltip>
    );
};

const HeaderWithTooltip = ({ label, tooltip }: { label: string, tooltip: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
        {label}
        <Tooltip title={tooltip} placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#64748b', cursor: 'help' }} />
        </Tooltip>
    </Box>
);

// --- SUB-COMPONENTE: TABLA DE HIJOS (LAZY LOADED) ---
const ChildrenTable: React.FC<{ parentId: string; viewMode: ViewMode; date: string }> = ({ parentId, viewMode, date }) => {
    const childGranularity = viewMode === 'products' ? 'customer_id' : 'product_id';
    
    const { data: children, isLoading } = useQuery({
        queryKey: ['breakdownChildren', parentId, viewMode, date],
        queryFn: () => getPredictionBreakdownChildren(date, childGranularity, parentId),
        staleTime: 1000 * 60 * 5 
    });

    if (isLoading) return <LinearProgress sx={{ bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: '#38bdf8' } }} />;
    if (!children || children.length === 0) return <Typography variant="caption" sx={{p:2, color: '#64748b'}}>Sin desglose disponible.</Typography>;

    return (
        <Box sx={{ margin: 2, ml: 6, p: 0, bgcolor: 'rgba(15, 23, 42, 0.4)', borderRadius: 2, border: '1px solid #334155', overflow: 'hidden' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}> {/* 'fixed' fuerza al navegador a respetar los anchos */}
                <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.2)' }}>
                        {/* COLUMNA 1: NOMBRE (Le damos el 50% del espacio para que quepan nombres largos) */}
                        <TableCell width="50%" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            {viewMode === 'products' ? 'CLIENTE' : 'PRODUCTO'}
                        </TableCell>
                        
                        {/* COLUMNA 2: FIABILIDAD (Ancho fijo del 15% para alinear verticalmente) */}
                        <TableCell width="15%" align="center" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            FIABILIDAD
                        </TableCell>
                        
                        {/* COLUMNAS DE DATOS (Resto del espacio) */}
                        <TableCell width="15%" align="right" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            VOL. ESTRATEGIA
                        </TableCell>
                        <TableCell width="20%" align="right" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            REV. ESTRATEGIA
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {children.map((child: ScenarioRow) => (
                        <TableRow key={child.entity_id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            {/* Añadimos 'noWrap' y 'textOverflow' por si el nombre es EXTREMADAMENTE largo */}
                            <TableCell component="th" scope="row" sx={{ color: '#e2e8f0', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <Tooltip title={child.entity_name} placement="top-start">
                                    <span>{child.entity_name}</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                                <SignalStrengthIndicator level={child.confidence_level} />
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#e2e8f0', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                {fmtNum(child.simulated_quantity)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#38bdf8', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                {fmtCurrency(child.revenue_projected)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

// --- SUB-COMPONENTE: FILA EXPANDIBLE PADRE ---
const ExpandableRow: React.FC<{ row: ScenarioRow; viewMode: ViewMode; date: string }> = ({ row, viewMode, date }) => {
  const [open, setOpen] = useState(false);
  const CELL_STYLE = { borderBottom: '1px solid #334155', color: '#f8fafc', py: 1.5 };
  
  const isProdView = viewMode === 'products';
  const isPositiveRev = row.delta_revenue >= 0;
  const isPositiveQty = row.delta_quantity >= 0;

  return (
    <>
      <TableRow hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02) !important' }, '& td': { borderBottom: open ? 'none' : '1px solid #334155' } }}>
        <TableCell sx={CELL_STYLE} width={50}>
            <IconButton aria-label="Ver detalle" size="small" onClick={() => setOpen(!open)} sx={{ color: open ? '#38bdf8' : '#64748b' }}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
        </TableCell>

        <TableCell sx={CELL_STYLE}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight="600" color="white">{row.entity_name}</Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace', fontSize: '0.7rem' }}>{row.entity_id}</Typography>
              </Box>
          </Box>
        </TableCell>
        
        {isProdView ? (
            <>
                <TableCell align="right" sx={CELL_STYLE}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" sx={{ color: '#fcd34d', fontWeight: 'bold' }}>{fmtNum(row.simulated_quantity)}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', textDecoration: 'line-through' }}>{fmtNum(row.base_quantity)}</Typography>
                    </Box>
                </TableCell>
                <TableCell align="right" sx={CELL_STYLE}>
                    <Typography variant="body2" sx={{ color: isPositiveQty ? '#4ade80' : '#ef4444', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {isPositiveQty ? <TrendingUpIcon fontSize="small"/> : <TrendingDownIcon fontSize="small"/>}
                        {row.delta_quantity > 0 ? '+' : ''}{fmtNum(row.delta_quantity)}
                    </Typography>
                </TableCell>
                <TableCell align="right" sx={CELL_STYLE}>
                    <Typography variant="body2" sx={{ color: '#38bdf8' }}>{fmtCurrency(row.revenue_projected)}</Typography>
                </TableCell>
            </>
        ) : (
            <>
                <TableCell align="right" sx={CELL_STYLE}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" sx={{ color: '#38bdf8', fontWeight: 'bold' }}>{fmtCurrency(row.revenue_projected)}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', textDecoration: 'line-through' }}>{fmtCurrency(row.revenue_bhs)}</Typography>
                    </Box>
                </TableCell>
                <TableCell align="right" sx={CELL_STYLE}>
                    <Typography variant="body2" sx={{ color: isPositiveRev ? '#4ade80' : '#ef4444', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {isPositiveRev ? '+' : ''}{fmtCurrency(row.delta_revenue)}
                    </Typography>
                </TableCell>
                <TableCell align="right" sx={CELL_STYLE}>
                    <Typography variant="body2" sx={{ color: '#fcd34d' }}>{fmtNum(row.simulated_quantity)} uds</Typography>
                </TableCell>
            </>
        )}
      </TableRow>
      
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: '1px solid #334155' }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
             {open && <ChildrenTable parentId={row.entity_id} viewMode={viewMode} date={date} />}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const BreakdownResultTable: React.FC<BreakdownResultTableProps> = ({
  data, isLoading, viewMode, currentDate, searchTerm
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const CELL_HEAD_STYLE = { bgcolor: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderBottom: '1px solid #334155', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 };

  // --- LÓGICA DE FILTRADO ---
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter(r => 
        (r.entity_name && r.entity_name.toLowerCase().includes(lower)) || 
        (r.entity_id && r.entity_id.toString().toLowerCase().includes(lower))
    );
  }, [data, searchTerm]);

  // Resetear página al buscar
  if (page > 0 && filteredData.length < page * rowsPerPage) {
      setPage(0);
  }

  if (isLoading) return <Box sx={{ p: 5, textAlign: 'center', bgcolor: '#1e293b', borderRadius: 2, border: '1px solid #334155' }}><CircularProgress sx={{ color: '#fcd34d' }} /></Box>;
  
  // Usamos filteredData para comprobar si está vacío (salvo que sea por búsqueda)
  if ((!data || data.length === 0) && !isLoading) return <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#1e293b', borderRadius: 2, border: '1px solid #334155' }}><Typography color="text.secondary">No hay datos de simulación disponibles.</Typography></Paper>;

  // PAGINAMOS SOBRE LOS DATOS YA FILTRADOS
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isProdView = viewMode === 'products';

  return (
    <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, bgcolor: '#1e293b', border: '1px solid #334155' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={CELL_HEAD_STYLE} width={50} />
              <TableCell sx={CELL_HEAD_STYLE}>
                  {isProdView ? 'PRODUCTO (Agregado)' : 'CLIENTE (Agregado)'}
              </TableCell>
              
              {isProdView ? (
                  <>
                    <TableCell align="right" sx={CELL_HEAD_STYLE}>
                        <HeaderWithTooltip label="Demanda (prevista vs. base)" tooltip="Unidades previstas con los precios recomendados frente a las previstas sin cambios de precio." />
                    </TableCell>
                    <TableCell align="right" sx={CELL_HEAD_STYLE}>
                        <HeaderWithTooltip label="Variación de demanda" tooltip="Cambio en unidades por el ajuste de precios." />
                    </TableCell>
                    <TableCell align="right" sx={CELL_HEAD_STYLE}>Facturación</TableCell>
                  </>
              ) : (
                  <>
                    <TableCell align="right" sx={CELL_HEAD_STYLE}>
                        <HeaderWithTooltip label="Facturación (prevista vs. base)" tooltip="Ingresos con los precios recomendados (en azul) frente a los previstos sin cambios de precio (tachado)." />
                    </TableCell>
                    <TableCell align="right" sx={CELL_HEAD_STYLE}>
                        <HeaderWithTooltip label="Impacto en facturación" tooltip="Diferencia de ingresos frente a no cambiar los precios." />
                    </TableCell>
                    <TableCell align="right" sx={CELL_HEAD_STYLE}>Volumen total</TableCell>
                  </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
                <ExpandableRow 
                    key={row.entity_id} 
                    row={row} 
                    viewMode={viewMode} 
                    date={currentDate}
                />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
        sx={{ color: '#94a3b8', borderTop: '1px solid #334155' }}
      />
    </Paper>
  );
};

export default BreakdownResultTable;